import type { Mix } from "@/types"

import { Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { ConnectionBanner, MixCard, Spinner, ScheduledMixCard } from "@/components/ui"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { useWebSocketContext } from '@/contexts'
import { MixLayout } from "@/components/layout"

export default function MixesPage() {
  const navigate = useNavigate()
  const { createInvitation, deleteMix, loading, mixes } = useWebSocketContext()

  const handleDeleteMix = async (mixId: string) => {
    deleteMix(mixId)
  }

  const handleShareMix = async (mixId: string) => {
    createInvitation(mixId)
  }

  return (

      <MixLayout>
        <div className="sticky top-0 bg-background z-30">
          <div className="flex justify-between items-center px-4 py-3">
            <h2 className="text-xl font-semibold text-foreground">
              <span className="flex items-center min-h-8">Mixes</span>
            </h2>
            <button
              onClick={() => navigate('/mixes/new')}
              className={`h-10 w-10 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer`}
            >
              <Plus className="w-5 h-5 inline" />
            </button>
          </div>
          <ConnectionBanner />
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center min-h-screen">
              <Spinner className="size-12" />
            </div>
          ) : mixes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-96 text-center px-4">
              <Card className="w-full max-w-sm">
                <CardHeader>
                  <CardTitle className="text-2xl">Welcome to KitchenMix! 🍳</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Ready to start your culinary journey? Create your first mix to organize recipes,
                    ingredients, and collaborate with friends on your favorite dishes.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <button
                    onClick={() => navigate('/mixes/new')}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium transition-colors shadow-sm w-full justify-center"
                  >
                    Create Your First Mix
                  </button>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 py-4 mx-4">
              {[...mixes].sort((a, b) => {
                if (a.updatedAt < b.updatedAt) return 1
                if (a.updatedAt > b.updatedAt) return -1
                return 0
              }).map((mix: Mix) => (
                mix.type === 'schedule' ? (
                  <ScheduledMixCard
                    key={mix.id}
                    mix={mix}
                    onShare={mix.ownership.isOwner ? () => handleShareMix(mix.id) : undefined}
                    onArchive={mix.ownership.isOwner ? () => handleDeleteMix(mix.id) : undefined}
                  />
                ) : (
                  <MixCard
                    key={mix.id}
                    mix={mix}
                    onShare={mix.ownership.isOwner ? () => handleShareMix(mix.id) : undefined}
                    onArchive={mix.ownership.isOwner ? () => handleDeleteMix(mix.id) : undefined}
                  />
                )
              ))}
            </div>
          )}
        </div>
      </MixLayout>

  )
}
